var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Select, message } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import EmptyComponent from 'components/EmptyComponent';
import DataTable from 'library/components/DataTable';
import { codeColumn, monoColumn, mutedColumn, NameText, RowActions, RowButton, statusColumn } from 'library/components/DataTable/cells';
import ListScreen, { ListAddButton, ListExportButton, listPager, usePagedRows } from 'library/components/ListScreen';
import localeTranslator from 'library/Hocs/LocalTranslator';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { ClinicRoom as ClinicRoomResource } from 'constants/resources';
import { ClinicRoom } from 'models';
import ClinicRoomDrawer from 'pages/Settings/drawers/ClinicRoomDrawer';
import services from 'services';
var csvEscape = function (value) { return "\"".concat(value.replace(/"/g, '""'), "\""); };
var downloadCsv = function (filename, rows) {
    var content = rows.map(function (row) { return row.map(csvEscape).join(','); }).join('\n');
    var blob = new Blob(["\uFEFF".concat(content)], { type: 'text/csv;charset=utf-8;' });
    var url = URL.createObjectURL(blob);
    var link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};
var ClinicRooms = function (props) {
    var t = props.translate;
    var navigate = useNavigate();
    var clinicRoomService = services.clinicRoomService, clinicService = services.clinicService, receptionDeskService = services.receptionDeskService, specializationService = services.specializationService, permissionService = services.permissionService;
    var _a = useState(''), searchValue = _a[0], setSearchValue = _a[1];
    var _b = useState('all'), clinicFilter = _b[0], setClinicFilter = _b[1];
    var _c = useState(false), exporting = _c[0], setExporting = _c[1];
    var _d = useState(false), drawerVisible = _d[0], setDrawerVisible = _d[1];
    var _e = useState(null), currentRoom = _e[0], setCurrentRoom = _e[1];
    var _f = useState(false), isEdit = _f[0], setIsEdit = _f[1];
    var debounceRef = useRef(undefined);
    var clinics = clinicService.useClinics().data;
    var receptionDesks = receptionDeskService.useReceptionDesks().data;
    var specializations = specializationService.useSpecializations().data;
    var canAdd = permissionService.hasPermissionToAdd(ClinicRoomResource);
    var canEdit = permissionService.hasPermissionToEdit(ClinicRoomResource);
    var canShow = permissionService.hasPermissionToShow(ClinicRoomResource);
    var _g = clinicRoomService.useClinicRooms(), clinicRooms = _g.data, isLoading = _g.isLoading;
    var rooms = clinicRooms || [];
    var clinicName = useCallback(function (clinicId) { var _a; return ((_a = clinics === null || clinics === void 0 ? void 0 : clinics.find(function (clinic) { return clinic.getId() === clinicId; })) === null || _a === void 0 ? void 0 : _a.getName()) || ''; }, [clinics]);
    var roomClinicId = function (room) { var _a; return Number(((_a = room.getReceptionDesk()) === null || _a === void 0 ? void 0 : _a.getClinicId()) || 0); };
    var filtered = useMemo(function () {
        var query = searchValue.trim().toLowerCase();
        return rooms.filter(function (room) {
            var _a, _b, _c;
            if (clinicFilter !== 'all' && roomClinicId(room) !== clinicFilter)
                return false;
            if (!query)
                return true;
            var status = t(room.isActive() ? 'clinicRooms.status.active' : 'clinicRooms.status.inactive');
            var haystack = [
                room.getName(),
                room.getCode(),
                (_a = room.getReceptionDesk()) === null || _a === void 0 ? void 0 : _a.getName(),
                clinicName((_b = room.getReceptionDesk()) === null || _b === void 0 ? void 0 : _b.getClinicId()),
                (_c = room.getSpecialization()) === null || _c === void 0 ? void 0 : _c.getName(),
                status
            ]
                .join(' ')
                .toLowerCase();
            return haystack.includes(query);
        });
    }, [rooms, clinicName, clinicFilter, searchValue, t]);
    var _h = usePagedRows(filtered), page = _h.page, setPage = _h.setPage, pages = _h.pages, from = _h.from, to = _h.to, pageRows = _h.pageRows;
    var clinicOptions = useMemo(function () { return __spreadArray([
        { value: 'all', label: t('clinicRooms.allClinics') }
    ], (clinics || []).map(function (clinic) { return ({
        value: clinic.getId(),
        label: clinic.getName()
    }); }), true); }, [clinics, t]);
    var onSearchValueChange = function (event) {
        var value = event.target.value;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(function () {
            setPage(1);
            setSearchValue(value);
        }, 300);
    };
    var openCreate = function () {
        var room = new ClinicRoom();
        room.setActive(true);
        setCurrentRoom(room);
        setIsEdit(false);
        setDrawerVisible(true);
    };
    var openEdit = useCallback(function (room) {
        setCurrentRoom(plainToInstance(ClinicRoom, instanceToPlain(room)));
        setIsEdit(true);
        setDrawerVisible(true);
    }, []);
    var onCloseDrawer = function () {
        setDrawerVisible(false);
        setCurrentRoom(null);
    };
    var exportRooms = function () {
        if (exporting)
            return;
        if (!filtered.length) {
            message.warning(t('clinicRooms.exportEmpty'));
            return;
        }
        setExporting(true);
        try {
            var rows = __spreadArray([
                [
                    t('clinicRooms.table.name'),
                    t('clinicRooms.table.code'),
                    t('clinicRooms.table.receptionDesk'),
                    t('clinicRooms.table.clinic'),
                    t('clinicRooms.table.specialization'),
                    t('clinicRooms.table.status')
                ]
            ], filtered.map(function (room) {
                var _a, _b, _c;
                return [
                    room.getName() || '',
                    room.getCode() || '',
                    ((_a = room.getReceptionDesk()) === null || _a === void 0 ? void 0 : _a.getName()) || '',
                    clinicName((_b = room.getReceptionDesk()) === null || _b === void 0 ? void 0 : _b.getClinicId()),
                    ((_c = room.getSpecialization()) === null || _c === void 0 ? void 0 : _c.getName()) || '',
                    t(room.isActive() ? 'clinicRooms.status.active' : 'clinicRooms.status.inactive')
                ];
            }), true);
            var stamp = new Date().toISOString().slice(0, 10);
            downloadCsv("clinic-rooms-".concat(stamp, ".csv"), rows);
        }
        catch (_a) {
            message.error(t('clinicRooms.exportFailed'));
        }
        finally {
            setExporting(false);
        }
    };
    var columns = useMemo(function () { return [
        monoColumn({ key: 'id', title: t('clinicRooms.id'), width: '64px', value: function (room) { return String(room.getId()).padStart(2, '0'); } }),
        {
            key: 'name',
            title: t('clinicRooms.table.name'),
            width: 'minmax(180px, 1.4fr)',
            render: function (room) {
                var _a;
                var desk = (_a = room.getReceptionDesk()) === null || _a === void 0 ? void 0 : _a.getName();
                return (_jsxs("div", { className: "cidx-who", children: [_jsx("span", { className: "cidx-tile ".concat(room.isActive() ? '' : 'is-muted'), children: _jsx(HomeOutlined, {}) }), _jsxs("div", { className: "cidx-stack", children: [_jsx(NameText, { children: room.getName() }), desk ? _jsx("span", { className: "cidx-stack__sub", children: desk }) : null] })] }));
            }
        },
        codeColumn({ key: 'code', title: t('clinicRooms.table.code'), width: '110px', value: function (room) { return room.getCode(); } }),
        mutedColumn({ key: 'spec', title: t('clinicRooms.table.specialization'), width: 'minmax(120px, 1fr)', value: function (room) { var _a; return (_a = room.getSpecialization()) === null || _a === void 0 ? void 0 : _a.getName(); } }),
        mutedColumn({ key: 'clinic', title: t('clinicRooms.table.clinic'), width: 'minmax(120px, 1fr)', value: function (room) { var _a; return clinicName((_a = room.getReceptionDesk()) === null || _a === void 0 ? void 0 : _a.getClinicId()); } }),
        statusColumn({
            title: t('clinicRooms.table.status'),
            active: function (room) { return room.isActive(); },
            activeLabel: t('clinicRooms.status.active'),
            inactiveLabel: t('clinicRooms.status.inactive')
        }),
        {
            key: 'actions',
            title: t('clinicRooms.table.actions'),
            width: canEdit ? '168px' : '88px',
            align: 'end',
            render: function (room) { return (_jsxs(RowActions, { children: [canShow ? _jsx(RowButton, { onClick: function () { return navigate("/clinic_rooms/".concat(room.getId())); }, children: t('clinicRooms.actions.show') }) : null, canEdit ? _jsx(RowButton, { onClick: function () { return openEdit(room); }, children: t('clinicRooms.actions.edit') }) : null] })); }
        }
    ]; }, [t, clinicName, canEdit, canShow, navigate, openEdit]);
    var clinicFilterControl = (clinics || []).length > 1 ? (_jsx(Select, { className: "cidx-select", value: clinicFilter, onChange: function (value) {
            setClinicFilter(value);
            setPage(1);
        }, options: clinicOptions, popupMatchSelectWidth: false })) : (clinics === null || clinics === void 0 ? void 0 : clinics[0]) ? (_jsxs("div", { className: "cidx-facility", children: [_jsxs("span", { className: "cidx-facility__label", children: [t('clinicRooms.facility'), ":"] }), _jsx("span", { children: clinics[0].getName() })] })) : null;
    return (_jsxs(ListScreen, { title: t('clinicRooms.title'), count: t('clinicRooms.countBadge', { count: rooms.length }), subtitle: t('clinicRooms.subtitle'), actions: (_jsxs(_Fragment, { children: [_jsx(ListExportButton, { label: t('clinicRooms.export'), busyLabel: t('clinicRooms.exporting'), busy: exporting, onClick: exportRooms }), canAdd && _jsx(ListAddButton, { label: t('clinicRooms.addRoom'), onClick: openCreate })] })), search: { placeholder: t('clinicRooms.searchPlaceholder'), onChange: onSearchValueChange }, filters: clinicFilterControl, children: [_jsx(DataTable, { minWidth: canEdit ? '1080px' : '960px', loading: isLoading, rows: pageRows, rowKey: function (room) { return room.getId(); }, onRowClick: canShow ? function (room) { return navigate("/clinic_rooms/".concat(room.getId())); } : undefined, empty: _jsx(EmptyComponent, { description: t('clinicRooms.empty') }), columns: columns, pager: listPager({
                    total: filtered.length,
                    page: page,
                    pages: pages,
                    meta: t('clinicRooms.showingRange', { from: from, to: to, total: filtered.length }),
                    previous: t('clinicRooms.previous'),
                    next: t('clinicRooms.next'),
                    center: t('clinicRooms.pageOf', { page: page, pages: pages }),
                    setPage: setPage
                }) }), _jsx(ClinicRoomDrawer, { visiable: drawerVisible, onCloseDrawer: onCloseDrawer, clinicRoom: currentRoom, clinics: clinics || [], receptionDesks: receptionDesks || [], specializations: specializations || [], isEdit: isEdit })] }));
};
export default localeTranslator(ClinicRooms, 'settings');
